(() => {
  if (customElements.get('carisma-better-carbs')) return;

  class CarismaBetterCarbs extends HTMLElement {
    connectedCallback() {
      // Wait for children when the theme editor inserts a new section instance.
      this.initFrame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected) return;
      this.events = new AbortController();
      const { signal } = this.events;
      this.addEventListener('click', (event) => this.playVideo(event), { signal });
      this.rail = this.querySelector('.cbc-benefits-rail');
      this.track = this.querySelector('.cbc-benefits-track');
      this.originals = Array.from(this.querySelectorAll('[data-cbc-original]'));
      if (!this.rail || !this.track || !this.originals.length) return;

      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', () => this.queueMeasure(), { signal });
      this.observer = new ResizeObserver(() => this.queueMeasure());
      this.observer.observe(this.rail);
      this.originals.forEach(item => this.observer.observe(item));
      this.measure();

      // Font loading can change the width of each benefit.
      if (document.fonts) {
        document.fonts.ready.then(() => {
          if (!signal.aborted) this.queueMeasure();
        });
      }
    }

    queueMeasure() {
      if (!this.isConnected || this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    }

    measure() {
      if (!this.isConnected) return;
      const railWidth = this.rail.getBoundingClientRect().width;
      const widths = this.originals.map(item => item.getBoundingClientRect().width);
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const loopDistance = widths.reduce((total, width) => total + width + gap, 0);
      if (railWidth <= 0 || loopDistance <= 0 || widths.some(width => width <= 0)) return;

      this.track.querySelectorAll('[data-cbc-clone]').forEach(copy => copy.remove());
      // Append benefits one by one, never group wrappers. At the loop endpoint,
      // the first copy occupies the original first item's position. Copies only
      // need to cover the viewport from there, including its last visible gap.
      const targetWidth = this.motion.matches ? 0 : railWidth + gap + 1;
      let copiedWidth = 0;
      let index = 0;
      const copies = document.createDocumentFragment();
      while (copiedWidth < targetWidth) {
        const originalIndex = index % this.originals.length;
        const copy = this.originals[originalIndex].cloneNode(true);
        copy.removeAttribute('data-cbc-original');
        copy.setAttribute('data-cbc-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('inert', '');
        // Only the source blocks may be selected/edited by Shopify.
        for (const node of [copy, ...copy.querySelectorAll('*')]) {
          node.removeAttribute('id');
          node.removeAttribute('data-shopify-editor-block');
          node.removeAttribute('data-shopify-editor-section');
        }
        copies.appendChild(copy);
        copiedWidth += widths[originalIndex] + gap;
        index += 1;
      }
      this.track.appendChild(copies);

      this.track.style.setProperty('--cbc-loop-distance', `${loopDistance}px`);
      this.rail.classList.add('is-ready');
      if (!this.motion.matches) this.rail.scrollLeft = 0;
    }

    playVideo(event) {
      const button = event.target.closest('[data-cbc-youtube-play], [data-cbc-native-play]');
      if (!button || !this.contains(button) || button.hidden) return;
      const caption = this.querySelector('[data-cbc-caption]');
      const focusPlayer = document.activeElement === button;
      let player;
      let container;

      if (button.hasAttribute('data-cbc-youtube-play')) {
        container = this.querySelector('[data-cbc-youtube]');
        const videoId = container?.dataset.videoId;
        if (!videoId) return;
        player = document.createElement('iframe');
        player.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&playsinline=1`;
        player.title = 'Carisma story video';
        player.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        player.allowFullscreen = true;
        container.replaceChildren(player);
      } else {
        container = this.querySelector('[data-cbc-native-video]');
        player = container?.querySelector('video');
        if (!player) return;
      }

      container.classList.add('is-active');
      button.hidden = true;
      if (caption) caption.hidden = true;
      if (focusPlayer) {
        player.tabIndex = 0;
        player.focus({ preventScroll: true });
      }
      if (player.tagName === 'VIDEO') {
        const playing = player.play();
        if (playing?.catch) playing.catch(() => {});
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
      this.events?.abort();
      this.observer?.disconnect();
    }
  }

  customElements.define('carisma-better-carbs', CarismaBetterCarbs);
})();
